import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faFilter, faFileImport, faPlus, faChevronLeft, faChevronRight, faSortUp, faSortDown, faSort } from '@fortawesome/free-solid-svg-icons';
import {
  widgetService,
  exportImportService,
  dashboardService,
  workflowService,
  dataSourceService,
  WidgetFilters,
} from '../services';
import { microsoftService } from '../services/microsoftService';
import { WidgetTemplate, WidgetType, SharedWidgetInfo, Dashboard, Workflow } from '../types';
import { WidgetBuilderModal } from '../components/widgets';
import ImportModal from '../components/common/ImportModal';
import PageTopBar from '../components/ui/PageTopBar';
import SearchBar from '../components/ui/SearchBar';
import Select from '../components/ui/Select';
import Button from '../components/ui/Button';
import Card, { CardBody } from '../components/ui/Card';
import ConfirmationModal from '../components/common/ConfirmationModal';
import { catalogEntries, catalogMap, allTags, getTagLabel } from '../components/widgets/catalog';
import { WidgetTableRow, TagFilterBar, WidgetPreviewModal } from './widgets/index';

// ---------------------------------------------------------------------------
// Page component
// ---------------------------------------------------------------------------
const Widgets: React.FC = () => {
  const [templates, setTemplates] = useState<WidgetTemplate[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [activeTags, setActiveTags] = useState<Set<string>>(new Set());
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingTemplate, setEditingTemplate] = useState<WidgetTemplate | null>(null);
  const [initialWidgetType, setInitialWidgetType] = useState<WidgetType | undefined>(undefined);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [isImportModalOpen, setIsImportModalOpen] = useState(false);
  const [exportingId, setExportingId] = useState<string | null>(null);
  const [sharedWidgets, setSharedWidgets] = useState<SharedWidgetInfo[]>([]);
  const [deletingTemplateId, setDeletingTemplateId] = useState<string | null>(null);
  const [previewTemplate, setPreviewTemplate] = useState<WidgetTemplate | null>(null);

  // Filter state
  const [selectedDashboard, setSelectedDashboard] = useState<string>('');
  const [selectedWorkflow, setSelectedWorkflow] = useState<string>('');
  const [dashboards, setDashboards] = useState<Dashboard[]>([]);
  const [workflows, setWorkflows] = useState<Workflow[]>([]);
  const [showFilters, setShowFilters] = useState(false);
  const [showTagDropdown, setShowTagDropdown] = useState(false);
  const [tagSearch, setTagSearch] = useState('');
  const tagDropdownRef = React.useRef<HTMLDivElement>(null);
  const tagSearchRef = React.useRef<HTMLInputElement>(null);

  // Sort state
  type SortField = 'name' | 'credentials' | 'updated';
  type SortDir = 'asc' | 'desc';
  const [sortField, setSortField] = useState<SortField>('name');
  const [sortDir, setSortDir] = useState<SortDir>('asc');

  const toggleSort = (field: SortField) => {
    if (sortField === field) {
      setSortDir(d => d === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDir(field === 'updated' ? 'desc' : 'asc');
    }
  };

  // External data access
  const [externalWidgets, setExternalWidgets] = useState<string[]>([]);
  const [userProviders, setUserProviders] = useState<string[]>([]);
  const [externalAccessLoaded, setExternalAccessLoaded] = useState(false);
  const [microsoftConnected, setMicrosoftConnected] = useState(false);

  // Load filter data and external access
  useEffect(() => {
    const loadFilterData = async () => {
      try {
        const [dashboardsRes, workflowsRes] = await Promise.all([
          dashboardService.getDashboards(1, 100),
          workflowService.getWorkflows(1, 100),
        ]);
        setDashboards(dashboardsRes.data);
        setWorkflows(workflowsRes.data);
      } catch (err) {
        console.error('Failed to load filter data:', err);
      }
    };

    const loadExternalAccess = async () => {
      try {
        const result = await dataSourceService.getAvailableWidgets();
        setExternalWidgets(result.widgets || []);
        setUserProviders(result.providers || []);
      } catch (err) {
        console.error('Failed to load external widget access:', err);
      } finally {
        setExternalAccessLoaded(true);
      }
    };

    const loadMicrosoftStatus = async () => {
      try {
        const status = await microsoftService.getConnectionStatus();
        setMicrosoftConnected(status.connected);
      } catch {
        // Not connected
      }
    };

    loadFilterData();
    loadExternalAccess();
    loadMicrosoftStatus();
  }, []);

  const hasActiveFilters = searchQuery || selectedDashboard || selectedWorkflow || activeTags.size > 0;
  const activeFilterCount =
    [selectedDashboard, selectedWorkflow].filter(Boolean).length
    + (searchQuery ? 1 : 0)
    + activeTags.size;

  const clearFilters = () => {
    setSearchQuery('');
    setSelectedDashboard('');
    setSelectedWorkflow('');
    setActiveTags(new Set());
  };

  // Filter templates by search + tags
  const filteredTemplates = useMemo(() => {
    return templates.filter(template => {
      // Skip dashboard_reference from the flat list
      if (template.type === 'dashboard_reference') return false;

      const entry = catalogMap.get(template.type);

      // Tag filter: widget must have ALL active tags
      if (activeTags.size > 0 && entry) {
        const widgetTags = new Set(entry.tags);
        const activeArr = Array.from(activeTags);
        for (let i = 0; i < activeArr.length; i++) {
          if (!widgetTags.has(activeArr[i])) return false;
        }
      }

      // Search: match name, description, type label, tags
      if (searchQuery) {
        const q = searchQuery.toLowerCase();
        const haystack = [
          template.name,
          template.description ?? '',
          entry?.definition.meta.label ?? '',
          entry?.definition.meta.description ?? '',
          ...(entry?.tags ?? []),
        ].join(' ').toLowerCase();
        if (!haystack.includes(q)) return false;
      }

      return true;
    });
  }, [templates, searchQuery, activeTags]);

  // Compute per-tag counts from the full (non-tag-filtered) template list
  const tagCounts = useMemo(() => {
    const counts = new Map<string, number>();
    for (const template of templates) {
      if (template.type === 'dashboard_reference') continue;
      const entry = catalogMap.get(template.type);
      if (!entry) continue;
      for (const tag of entry.tags) {
        counts.set(tag, (counts.get(tag) ?? 0) + 1);
      }
    }
    return counts;
  }, [templates]);

  // Sort
  const sortedTemplates = useMemo(() => {
    const sorted = [...filteredTemplates];
    sorted.sort((a, b) => {
      let cmp = 0;
      switch (sortField) {
        case 'name':
          cmp = a.name.localeCompare(b.name);
          break;
        case 'credentials': {
          const getCredStatus = (t: WidgetTemplate) => {
            const entry = catalogMap.get(t.type);
            if (!entry?.tags.includes('requires-credentials')) return 0; // not required
            const dpWidget = entry.definition.dataProvider?.widget;
            const isDpConnected = dpWidget && externalWidgets.includes(dpWidget);
            const isMsConnected = entry.category === 'microsoft' && microsoftConnected;
            return isDpConnected || isMsConnected ? 1 : 2; // connected / required
          };
          cmp = getCredStatus(a) - getCredStatus(b);
          break;
        }
        case 'updated': {
          const getUpdated = (t: WidgetTemplate) => {
            const entry = catalogMap.get(t.type);
            return new Date(entry?.definition.meta.lastUpdated || t.updatedAt).getTime();
          };
          cmp = getUpdated(a) - getUpdated(b);
          break;
        }
      }
      return sortDir === 'asc' ? cmp : -cmp;
    });
    return sorted;
  }, [filteredTemplates, sortField, sortDir, externalWidgets, microsoftConnected]);

  // Pagination
  const PAGE_SIZE = 20;
  const totalFilteredPages = Math.max(1, Math.ceil(sortedTemplates.length / PAGE_SIZE));
  const paginatedTemplates = useMemo(() => {
    const start = (page - 1) * PAGE_SIZE;
    return sortedTemplates.slice(start, start + PAGE_SIZE);
  }, [sortedTemplates, page]);

  // Reset page when filters change
  useEffect(() => {
    setPage(1);
  }, [searchQuery, activeTags, selectedDashboard, selectedWorkflow]);

  // Close tag dropdown on outside click
  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (tagDropdownRef.current && !tagDropdownRef.current.contains(e.target as Node)) {
        setShowTagDropdown(false);
        setTagSearch('');
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const toggleTag = (tag: string) => {
    setActiveTags(prev => {
      const next = new Set(prev);
      if (next.has(tag)) {
        next.delete(tag);
      } else {
        next.add(tag);
      }
      return next;
    });
  };

  const loadTemplates = useCallback(async () => {
    try {
      setLoading(true);
      const filters: WidgetFilters = {};
      if (selectedDashboard) filters.dashboardId = selectedDashboard;
      if (selectedWorkflow) filters.workflowId = selectedWorkflow;

      const response = await widgetService.getWidgetTemplates(page, 100, filters);
      setTemplates(response.data);
      setTotalPages(Math.ceil(response.total / response.pageSize));
    } catch (err: any) {
      const detail = err.response?.data?.detail;
      if (Array.isArray(detail)) {
        setError(detail.map((e: any) => e.msg || JSON.stringify(e)).join(', '));
      } else {
        setError(typeof detail === 'string' ? detail : 'Failed to load widget templates');
      }
    } finally {
      setLoading(false);
    }
  }, [page, selectedDashboard, selectedWorkflow]);

  const loadSharedWidgets = async () => {
    try {
      const response = await dashboardService.getSharedWidgets();
      setSharedWidgets(response.sharedWidgets);
    } catch {
      // Silently handle errors
    }
  };

  useEffect(() => {
    loadTemplates();
    loadSharedWidgets();
  }, [loadTemplates]);

  const handleEdit = (template: WidgetTemplate) => {
    setEditingTemplate(template);
    setIsModalOpen(true);
  };

  const confirmDeleteTemplate = async () => {
    if (!deletingTemplateId) return;
    try {
      await widgetService.deleteWidgetTemplate(deletingTemplateId);
      loadTemplates();
    } catch (err: any) {
      const detail = err.response?.data?.detail;
      setError(typeof detail === 'string' ? detail : 'Failed to delete template');
    }
  };

  const handleModalClose = () => {
    setIsModalOpen(false);
    setEditingTemplate(null);
    setInitialWidgetType(undefined);
  };

  const handleSave = () => {
    handleModalClose();
    loadTemplates();
  };

  const handleExport = async (id: string) => {
    try {
      setExportingId(id);
      await exportImportService.exportWidget(id);
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : 'Failed to export widget');
    } finally {
      setExportingId(null);
    }
  };

  const handleImportComplete = () => {
    setIsImportModalOpen(false);
    loadTemplates();
  };

  // Determine which templates can be deleted (non-system)
  const canDeleteType = (type: WidgetType): boolean => {
    const entry = catalogMap.get(type);
    if (!entry) return true;
    return !entry.definition.meta.isSystem;
  };

  return (
    <div className="h-full flex flex-col bg-neutral-50">
      <PageTopBar title="Widget Library" description="Browse available widgets" />
      <div className="flex-1 overflow-y-auto">
      <div className="w-full px-4 sm:px-6 lg:px-8 py-8">

        {/* Search + Filters + Tags */}
        <div className="mb-6 space-y-3">
          <div className="flex items-center gap-3">
            <SearchBar
              value={searchQuery}
              onChange={setSearchQuery}
              placeholder="Search widgets..."
            />

            {/* Tag dropdown */}
            <div className="relative" ref={tagDropdownRef}>
              <button
                onClick={() => setShowTagDropdown(!showTagDropdown)}
                className={`h-9 flex items-center gap-2 px-3 rounded-lg border text-sm transition-colors ${
                  activeTags.size > 0
                    ? 'border-primary-300 bg-primary-50 text-primary-700'
                    : 'border-neutral-300 bg-surface text-neutral-700 hover:bg-neutral-50'
                }`}
              >
                <span className="text-sm">
                  {activeTags.size > 0 ? `Tags (${activeTags.size})` : 'Tags'}
                </span>
                <svg
                  className={`w-4 h-4 transition-transform ${showTagDropdown ? 'rotate-180' : ''}`}
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                </svg>
              </button>

              {showTagDropdown && (() => {
                if (!tagSearchRef.current) setTimeout(() => tagSearchRef.current?.focus(), 0);
                const filteredTags = tagSearch
                  ? allTags.filter(t => getTagLabel(t).toLowerCase().includes(tagSearch.toLowerCase()))
                  : allTags;
                return (
                  <div className="absolute top-full left-0 mt-1 w-56 bg-surface rounded-lg shadow-lg border border-neutral-200 z-50">
                    <div className="p-2 border-b border-neutral-200">
                      <input
                        ref={tagSearchRef}
                        type="text"
                        value={tagSearch}
                        onChange={e => setTagSearch(e.target.value)}
                        placeholder="Search tags..."
                        className="w-full px-2 py-1.5 text-sm border border-neutral-200 rounded-md bg-surface text-neutral-900 placeholder-neutral-400 focus:outline-none"
                      />
                    </div>
                    <div className="p-2 max-h-60 overflow-y-auto">
                      {!tagSearch && (
                        <button
                          onClick={() => setActiveTags(new Set())}
                          className={`w-full flex items-center justify-between px-3 py-2 rounded-md text-left transition-colors ${
                            activeTags.size === 0
                              ? 'bg-primary-50 text-primary-700'
                              : 'hover:bg-neutral-100 text-neutral-700'
                          }`}
                        >
                          <span className="text-sm">All Tags</span>
                        </button>
                      )}
                      {filteredTags.map(tag => (
                        <button
                          key={tag}
                          onClick={() => toggleTag(tag)}
                          className={`w-full flex items-center justify-between px-3 py-2 rounded-md text-left transition-colors ${
                            activeTags.has(tag)
                              ? 'bg-primary-50 text-primary-700'
                              : 'hover:bg-neutral-100 text-neutral-700'
                          }`}
                        >
                          <span className="text-sm">{getTagLabel(tag)}</span>
                          <span className="text-xs text-neutral-400">{tagCounts.get(tag) ?? 0}</span>
                        </button>
                      ))}
                      {filteredTags.length === 0 && (
                        <p className="px-3 py-2 text-sm text-neutral-400">No matches</p>
                      )}
                    </div>
                  </div>
                );
              })()}
            </div>

            <button
              onClick={() => setShowFilters(!showFilters)}
              className={`h-9 px-3 border rounded-lg flex items-center gap-2 transition-all duration-fast text-sm font-medium ${
                showFilters || selectedDashboard || selectedWorkflow
                  ? 'bg-primary-50 border-primary-300 text-primary-700'
                  : 'bg-surface border-neutral-200 text-neutral-700 hover:bg-neutral-50'
              }`}
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M3 4a1 1 0 011-1h16a1 1 0 011 1v2.586a1 1 0 01-.293.707l-6.414 6.414a1 1 0 00-.293.707V17l-4 4v-6.586a1 1 0 00-.293-.707L3.293 7.293A1 1 0 013 6.586V4z" />
              </svg>
              Filters
              {activeFilterCount > 0 && (
                <span className="bg-primary-600 text-white text-xs px-1.5 py-0.5 rounded-full min-w-[18px] text-center">
                  {activeFilterCount}
                </span>
              )}
            </button>
            {hasActiveFilters && (
              <Button variant="outline" size="sm" onClick={clearFilters}>
                Clear all
              </Button>
            )}
          </div>

          {showFilters && (
            <Card variant="default" className="p-4">
              <div className="flex items-center gap-4">
                <Select
                  value={selectedDashboard}
                  onChange={e => setSelectedDashboard(e.target.value)}
                  options={dashboards.map(d => ({ value: d.id, label: d.name }))}
                  placeholder="All dashboards"
                  label="In Dashboard"
                  size="sm"
                  className="flex-1 max-w-xs"
                />
                <Select
                  value={selectedWorkflow}
                  onChange={e => setSelectedWorkflow(e.target.value)}
                  options={workflows.map(w => ({ value: w.id, label: w.name }))}
                  placeholder="All workflows"
                  label="In Workflow"
                  size="sm"
                  className="flex-1 max-w-xs"
                />
              </div>
            </Card>
          )}

        </div>

        {/* Error */}
        {error && (
          <div className="mb-6 p-4 bg-danger-50 border border-danger-200 rounded-lg text-danger-700 flex items-center justify-between">
            <span>{error}</span>
            <button onClick={() => setError(null)} className="text-danger-500 hover:text-danger-700 font-medium">
              Dismiss
            </button>
          </div>
        )}

        {/* Loading */}
        {loading ? (
          <div className="flex justify-center items-center h-64">
            <div className="animate-spin rounded-full h-8 w-8 border-2 border-primary-600 border-t-transparent" />
          </div>
        ) : (
          <Card variant="default" className="overflow-hidden">
            {filteredTemplates.length > 0 ? (
              <table className="w-full table-fixed">
                <colgroup>
                  <col className="w-[18%]" />           {/* Widget */}
                  <col className="w-[12%]" />           {/* Credentials */}
                  <col className="w-[10%] hidden md:table-column" />  {/* Updated */}
                  <col className="w-[25%] hidden md:table-column" />  {/* Tags */}
                  <col className="hidden lg:table-column" />          {/* Description - takes remaining */}
                  <col className="w-16 hidden lg:table-column" />     {/* Spacer */}
                </colgroup>
                <thead className="bg-neutral-50">
                  <tr>
                    {([['name', 'Widget', ''], ['credentials', 'Credentials', ''], ['updated', 'Updated', 'hidden md:table-cell']] as const).map(([field, label, extra]) => (
                      <th
                        key={field}
                        className={`px-6 py-3 text-left text-xs font-medium text-neutral-500 uppercase tracking-wider cursor-pointer select-none hover:text-neutral-700 transition-colors ${extra}`}
                        onClick={() => toggleSort(field)}
                      >
                        <span className="inline-flex items-center gap-1.5">
                          {label}
                          <FontAwesomeIcon
                            icon={sortField === field ? (sortDir === 'asc' ? faSortUp : faSortDown) : faSort}
                            className={`w-3 h-3 ${sortField === field ? 'text-primary-500' : 'text-neutral-300'}`}
                          />
                        </span>
                      </th>
                    ))}
                    <th className="px-6 py-3 text-left text-xs font-medium text-neutral-500 uppercase tracking-wider hidden md:table-cell">Tags</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-neutral-500 uppercase tracking-wider hidden lg:table-cell">Description</th>
                    <th className="px-6 py-3 hidden lg:table-cell w-16"></th>
                  </tr>
                </thead>
                <tbody>
                  {paginatedTemplates.map(template => (
                    <WidgetTableRow
                      key={template.id}
                      template={template}
                      canDelete={canDeleteType(template.type)}
                      exportingId={exportingId}
                      externalWidgets={externalWidgets}
                      microsoftConnected={microsoftConnected}
                      onPreview={setPreviewTemplate}
                      onEdit={handleEdit}
                      onExport={handleExport}
                      onDelete={setDeletingTemplateId}
                    />
                  ))}
                </tbody>
              </table>
            ) : (
              <div className="text-center py-16">
                <h3 className="text-lg font-medium text-neutral-900 mb-2">
                  {hasActiveFilters ? 'No widgets match your filters' : 'No widgets found'}
                </h3>
                <p className="text-sm text-neutral-500 mb-6 max-w-sm mx-auto">
                  {hasActiveFilters
                    ? 'Try adjusting your search, tags, or filter criteria'
                    : 'Create your first widget to get started'}
                </p>
                {hasActiveFilters && (
                  <Button variant="secondary" onClick={clearFilters}>Clear all filters</Button>
                )}
              </div>
            )}
          </Card>
        )}

        {/* Pagination + results count */}
        {!loading && sortedTemplates.length > 0 && (
          <div className="mt-4 flex items-center justify-between">
            <div className="text-xs text-neutral-400">
              Showing {(page - 1) * PAGE_SIZE + 1}–{Math.min(page * PAGE_SIZE, sortedTemplates.length)} of {sortedTemplates.length} widgets
              {sortedTemplates.length < templates.length && ` (${templates.length} total)`}
            </div>
            {totalFilteredPages > 1 && (
              <nav className="flex items-center gap-1">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setPage(p => Math.max(1, p - 1))}
                  disabled={page === 1}
                  icon={<FontAwesomeIcon icon={faChevronLeft} className="w-3 h-3" />}
                >
                  Previous
                </Button>
                <div className="flex items-center gap-1 px-2">
                  {Array.from({ length: totalFilteredPages }, (_, i) => i + 1).map(pageNum => (
                    <button
                      key={pageNum}
                      onClick={() => setPage(pageNum)}
                      className={`w-8 h-8 text-sm font-medium rounded-lg transition-colors ${
                        pageNum === page
                          ? 'bg-primary-600 text-white'
                          : 'text-neutral-500 hover:bg-neutral-100'
                      }`}
                    >
                      {pageNum}
                    </button>
                  ))}
                </div>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setPage(p => Math.min(totalFilteredPages, p + 1))}
                  disabled={page === totalFilteredPages}
                  icon={<FontAwesomeIcon icon={faChevronRight} className="w-3 h-3" />}
                  iconPosition="right"
                >
                  Next
                </Button>
              </nav>
            )}
          </div>
        )}
      </div>
      </div>

      {/* Preview Modal */}
      <WidgetPreviewModal
        isOpen={previewTemplate !== null}
        onClose={() => setPreviewTemplate(null)}
        template={previewTemplate}
        onExport={handleExport}
        exportingId={exportingId}
      />

      {/* Widget Builder Modal */}
      <WidgetBuilderModal
        isOpen={isModalOpen}
        onClose={handleModalClose}
        onSave={handleSave}
        template={editingTemplate}
        initialType={initialWidgetType}
      />

      {/* Import Modal */}
      <ImportModal
        isOpen={isImportModalOpen}
        onClose={() => setIsImportModalOpen(false)}
        onImportComplete={handleImportComplete}
        allowedTypes={['widget']}
      />

      <ConfirmationModal
        isOpen={deletingTemplateId !== null}
        onClose={() => setDeletingTemplateId(null)}
        onAction={confirmDeleteTemplate}
        title="Delete Widget Template"
        description="Are you sure you want to delete this widget template?"
        actionText="Delete"
      />
    </div>
  );
};

export default Widgets;
